import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useToggle } from '~/hooks';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { useReviewNavigation } from './useReviewNavigation';
import { useReviewSubmission } from './useReviewSubmission';

export const useReviewConfirm = () => {
  const { control, setValue, getValues } = useFormContext<IShiftDetailsFormValues>();

  const dates = useWatch({ control, name: 'dates' });
  const officeId = useWatch({ control, name: 'officeId' });
  const professionType = useWatch({ control, name: 'professionType' });
  const preferredSpecialty = useWatch({ control, name: 'preferredSpecialty' });
  const arrivalTime = useWatch({ control, name: 'arrivalTime' });
  const departureTime = useWatch({ control, name: 'departureTime' });
  const mealBreakDuration = useWatch({ control, name: 'mealBreakDuration' });
  const isMealBreakPaid = useWatch({ control, name: 'isMealBreakPaid' });
  const rate = useWatch({ control, name: 'rate' });
  const isAssistedHygiene = useWatch({ control, name: 'isAssistedHygiene' });
  const shiftOverrides = useWatch({ control, name: 'shiftOverrides' });

  const sortedDates = useMemo(
    () =>
      dates
        .map((date, index) => ({ date, originalIndex: index }))
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    [dates],
  );

  const [dateToDelete, setDateToDelete] = useState<Date | null>(null);
  const [acceptedGuidelines, acceptedGuidelinesControls] = useToggle();
  const [showLeavingPopup, leavingPopupControls] = useToggle();

  const navigation = useReviewNavigation();

  const submission = useReviewSubmission({
    officeId,
    allowNavigation: navigation.allowNavigation,
    getValues,
    arrivalTime,
    departureTime,
    mealBreakDuration,
    isMealBreakPaid,
  });

  useEffect(() => {
    if (dates.length === 0) {
      navigation.redirectToSelectDates();
    }
  }, [dates.length, navigation]);

  const handleDeleteDate = useCallback(() => {
    if (!dateToDelete) return;
    const newDates = dates.filter((d) => d.getTime() !== dateToDelete.getTime());
    setValue('dates', newDates);
    setDateToDelete(null);
  }, [dateToDelete, dates, setValue]);

  return {
    // Form values
    officeId,
    sortedDates,
    professionType,
    preferredSpecialty,
    arrivalTime,
    departureTime,
    mealBreakDuration,
    isMealBreakPaid,
    rate,
    isAssistedHygiene,
    shiftOverrides,

    // UI state
    dateToDelete,
    acceptedGuidelines,
    showLeavingPopup,

    // State controls
    setDateToDelete,
    acceptedGuidelinesControls,
    leavingPopupControls,

    // Handlers
    handleDeleteDate,

    // Navigation
    ...navigation,

    // Submission
    ...submission,
  };
};
